import { useEffect, useCallback } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  callback: (event: KeyboardEvent) => void;
  description?: string;
  preventDefault?: boolean;
}

export interface UseKeyboardShortcutsOptions {
  enabled?: boolean;
  shortcuts: KeyboardShortcut[];
}

/**
 * Hook for managing keyboard shortcuts
 * 
 * @example
 * useKeyboardShortcuts({
 *   shortcuts: [
 *     {
 *       key: 'k',
 *       metaKey: true,
 *       callback: () => openSearch(),
 *       description: 'Open search',
 *       preventDefault: true
 *     }
 *   ]
 * });
 */
export const useKeyboardShortcuts = ({ 
  enabled = true, 
  shortcuts 
}: UseKeyboardShortcutsOptions) => {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Check each shortcut
    for (const shortcut of shortcuts) {
      const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();
      const ctrlMatches = shortcut.ctrlKey ? event.ctrlKey : !event.ctrlKey;
      const metaMatches = shortcut.metaKey ? event.metaKey : !event.metaKey;
      const shiftMatches = shortcut.shiftKey ? event.shiftKey : !event.shiftKey;
      const altMatches = shortcut.altKey ? event.altKey : !event.altKey;

      if (keyMatches && ctrlMatches && metaMatches && shiftMatches && altMatches) {
        if (shortcut.preventDefault !== false) {
          event.preventDefault();
        }
        shortcut.callback(event);
        break; // Only trigger first matching shortcut
      }
    }
  }, [enabled, shortcuts]);

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, handleKeyDown]);
};

/**
 * Global keyboard shortcuts that should be available app-wide
 */
export const GLOBAL_SHORTCUTS = {
  SEARCH: {
    key: 'k',
    metaKey: true,
    description: '⌘K to search',
  },
  ESCAPE: {
    key: 'Escape',
    description: 'ESC to close',
  },
  NEW_PATIENT: {
    key: 'n',
    metaKey: true,
    shiftKey: true,
    description: '⌘⇧N to add patient',
  },
  NEW_APPOINTMENT: {
    key: 'a',
    metaKey: true,
    shiftKey: true,
    description: '⌘⇧A to add appointment',
  },
  HELP: {
    key: '?',
    shiftKey: true,
    description: '? to show help',
  },
} as const;

/**
 * Get shortcut display string for UI
 */
export const getShortcutDisplay = (shortcut: {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
}): string => {
  const parts: string[] = [];
  
  if (shortcut.metaKey) parts.push('⌘');
  if (shortcut.ctrlKey) parts.push('Ctrl');
  if (shortcut.shiftKey) parts.push('⇧');
  if (shortcut.altKey) parts.push('⌥');
  
  parts.push(shortcut.key.toUpperCase());
  
  return parts.join(' + ');
};
